import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import LanguageToggle from "./language-toggle/LanguageToggle";
import EnglishRevertModal from "./english-revert-modal/EnglishRevertModal";
import SpanishAcknowledgmentModal from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { DEFAULT_LANGUAGE, LANG_PREF_COOKIE_NAME, LOCAL_STORAGE_KEY, SPANISH_LANG_CODE, SUPPORTED_LANGUAGES } from "utilities/Constant";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { selectHasLoans } from "features/preferredLanguageSlice";
import { getCurrentUserId, getDealApplicationId, getDealBorrowerId, selectPreferredLanguage, setPreferredLanguage } from "features/authSlice";
import { extractDealServicePreferredLanguage, getPreferredLanguage, toUserDbPreferredLanguage } from "utilities/getPreferredLanguage";
import { useGetBorrowerDetailsQuery, useGetBorrowerPersonalInformationQuery, useUpdateUserPreferredLanguageMutation } from "pages/dashboard/home/dashboardService";

interface PreferredLanguageProps {
  readonly isMobile?: boolean;
  readonly isAuthenticated?: boolean;
}

const setLangPrefCookie = (langCode: string): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=${langCode};path=/`;
};

const clearLangPrefCookie = (): void => {
  document.cookie = `${LANG_PREF_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const setLocalStorage = (key: string, value: string): void => {
  window.localStorage.setItem(key, value);
};

const getLocalStorage = (key: string): string | null => {
  return window.localStorage.getItem(key);
};

const PreferredLanguage: React.FC<PreferredLanguageProps> = ({ isMobile = false, isAuthenticated = false }) => {
  const dispatch = useDispatch();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const dealBorrowerId = useSelector(getDealBorrowerId);
  const hasLoans = useSelector(selectHasLoans);
  const hasApplicationContext = Boolean(dealApplicationId);
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();
  const { data: userProfile, isSuccess: isUserProfileSuccess } = useGetBorrowerDetailsQuery(userId, {
    skip:  !userId,
  });
  const { data: dealServicePersonalInformation } = useGetBorrowerPersonalInformationQuery(
    { borrowerId: dealBorrowerId || "" },
    { skip: !isAuthenticated || !hasApplicationContext || !dealBorrowerId },
  );

  const [userLanguage, setUserLanguage] = useState<string>(() => getPreferredLanguage(undefined));
  const [showEnglishRevertModal, setShowEnglishRevertModal] = useState(false);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const [hasAcknowledgedSpanishPrompt, setHasAcknowledgedSpanishPrompt] = useState(false);

  // Sync toggle when Redux language changes (e.g. after login resolves server value)
  useEffect(() => {
    const dealServiceLanguage = extractDealServicePreferredLanguage(dealServicePersonalInformation);
    const resolved = dealServiceLanguage
      ? getPreferredLanguage(dealServiceLanguage)
      : getPreferredLanguage(reduxLanguage);

    const currentUserDbLanguage = userProfile?.preferredLanguage;
    const isUserDbLanguageNull =
      isUserProfileSuccess &&
      (currentUserDbLanguage == null || currentUserDbLanguage.trim() === "");
    const shouldPromptSpanishAcknowledgment =
      isAuthenticated &&
      resolved === "Español" && 
      isUserDbLanguageNull &&
      !hasAcknowledgedSpanishPrompt &&
      !showAcknowledgmentModal; 

    if (!shouldPromptSpanishAcknowledgment) {
      setUserLanguage(resolved);
    }

    if (shouldPromptSpanishAcknowledgment) { 
      setPendingLanguage(resolved);
      setShowAcknowledgmentModal(true);
    }
  }, [
    dealServicePersonalInformation,
    reduxLanguage,
    isAuthenticated,
    isUserProfileSuccess,
    userProfile?.preferredLanguage,
    hasAcknowledgedSpanishPrompt,
    showAcknowledgmentModal,
  ]);

  
  useEffect(() => {
    const savedLanguage = getLocalStorage(LOCAL_STORAGE_KEY);
    if (savedLanguage === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  }, []);

  if (!featureFlag?.preferredLanguageEnabled) {
    return null;
  }

  // Never show modals on unauthenticated pages, even if Redux state has language
  const shouldShowModals = isAuthenticated;

  const applyLanguageChange = (value: string) => {
    setUserLanguage(value);
    setLocalStorage(LOCAL_STORAGE_KEY, value);

    if (isAuthenticated) {
      dispatch(setPreferredLanguage(value));
    }
     if (value === "Español") {
      setLangPrefCookie(SPANISH_LANG_CODE);
    } else {
      clearLangPrefCookie();
    }
  };

  const persistLanguageChange = (value: string) => {
    if (!isAuthenticated || !userId) {
      return;
    }

    void updateUserPreferredLanguage({
      userId,
      preferredLanguage: toUserDbPreferredLanguage(value),
    });
  };

  const commitLanguageChange = (value: string) => {
    applyLanguageChange(value);
    persistLanguageChange(value);
  };

  const handleLanguageChange = (value: string) => {
    if (!isAuthenticated) {
      setUserLanguage(value);
      setShowAcknowledgmentModal(false);
      setShowEnglishRevertModal(false);
      return;
    }

    if (value === "Español" && userLanguage !== "Español") {
      setPendingLanguage(value);
      setShowAcknowledgmentModal(true);
      return;
    }

    if (value === DEFAULT_LANGUAGE && userLanguage !== DEFAULT_LANGUAGE) {
      setShowEnglishRevertModal(true);
      return;
    }

    commitLanguageChange(value);
  };

  const handleAcknowledge = () => {
    if (pendingLanguage) {
      commitLanguageChange(pendingLanguage);
      setPendingLanguage(null);
    }
    setHasAcknowledgedSpanishPrompt(true);
    setShowAcknowledgmentModal(false);
  };

  const handleRevertConfirm = () => {
    setShowEnglishRevertModal(false);
    commitLanguageChange(DEFAULT_LANGUAGE);
  };

  const handleRevertCancel = () => {
    setShowEnglishRevertModal(false);
  };

  const isDisabled = isAuthenticated ? !!hasLoans : false;

  return (
    <>
      <div className="preferred-language">
        <LanguageToggle value={userLanguage} onChange={handleLanguageChange} options={SUPPORTED_LANGUAGES} isMobile={isMobile} disabled={isDisabled} />
      </div>
      {shouldShowModals && showAcknowledgmentModal && (
        <SpanishAcknowledgmentModal
          onAcknowledge={handleAcknowledge}
          onCancel={() => {
            setShowAcknowledgmentModal(false);
            setPendingLanguage(null);
          }}
        />
      )}
      {shouldShowModals && showEnglishRevertModal && (
        <EnglishRevertModal onConfirm={handleRevertConfirm} onCancel={handleRevertCancel} />
      )}
    </>
  );
};

export default PreferredLanguage;
